// FROM THE SIGN-IN SEAM'S TYPED REFUSALS TO THE APPROVED SENTENCES (ACC-3a; ACCOUNTS_PLAN §3, C1–C12 and D5).
//
// The seam (`services/auth.ts`) answers codes and never a sentence; this is the one table from a code to the words an
// owner reads. Every sentence is a held one (`components/heldCopy.ts`), approved 2026-09-30 ("Sentences approved"), or
// WEB-COPY-1 item 7 for C9.
//
// NO ENUMERATION (C1). "Wrong password" and "no account with that email" are ONE refusal in the seam
// (`wrongPasswordOrNoAccount`, from every code Firebase can answer for either) and ONE sentence here, so nothing on the
// page tells an owner — or anyone else — whether an address has an account. `test/accountScreens.test.tsx` checks both
// from the screen, and every Firebase code for either from this table.
//
// THE FALLBACK IS C9, AND ITS USES ARE LISTED. A refusal with no sentence of its own — one a screen should not reach
// (`alreadySignedIn`, `notSignedIn`), one the server or Firebase answers rarely (`emailsUnavailable`,
// `appleCredentialUnavailable`, `wrongAccount`), or a code nobody mapped (`unknown`, R112's alarm) — shows C9, "We
// couldn't reach PROppeal's servers just now…", the approved sentence for a request that did not go through.
// `popupBlocked` and `accountDisabled` have their own words since 2026-10-01 (F-ACC3a-2, Tyler: "yes those sentences look
// good"; `ACCOUNT_FOLLOW_UP_COPY` in `heldCopy.ts`) and are no longer C9's.
// `popupClosed` shows nothing: the owner closed Apple's window on purpose.

import type { AuthRefusal } from "../../services";
import type { AnyHeldId } from "../../components/heldCopy";

/** The kinds that fall back to C9 because no approved sentence names them. Exported for the test that lists them. */
export const C9_FALLBACK: readonly AuthRefusal["kind"][] = [
  "network", "wrongAccount", "alreadySignedIn", "alreadyLinked", "notSignedIn", "wrongProvider",
  "alreadyVerified", "appleCredentialUnavailable", "emailsUnavailable", "unknown",
];

/**
 * The held sentence for a refusal, or null for one the owner should not be told about (`popupClosed`). `reauthWith` is
 * how this account re-enters, for C6's two forms.
 */
export function refusalCopy(refusal: AuthRefusal, reauthWith: "password" | "apple" | null = "password"): AnyHeldId | null {
  switch (refusal.kind) {
    case "wrongPasswordOrNoAccount":
      return "account.error.noMatch"; // C1
    case "emailHasAccount":
      return "account.error.emailHasAccount"; // C2
    case "tooManyTries":
      return "account.error.tooManyTries"; // C3
    case "passwordTooShort":
      return "account.error.passwordTooShort"; // C4
    case "notAnEmail":
      return "account.error.notAnEmail"; // C5
    case "reauthRequired":
      return reauthWith === "apple" ? "account.error.reauthApple" : "account.error.reauthPassword"; // C6
    case "emailHasPasswordAccount":
      return "account.error.emailHasPasswordAccount"; // C10
    case "appleIdIsAnotherAccount":
      return "account.error.appleIdIsAnotherAccount"; // C11
    case "linkExpired":
      return "account.link.expired"; // D5
    case "popupClosed":
      return null;
    case "popupBlocked":
      return "account.error.popupBlocked"; // F-ACC3a-2, approved 2026-10-01
    case "accountDisabled":
      return "account.error.accountDisabled"; // F-ACC3a-2, approved 2026-10-01
    case "network":
    case "wrongAccount":
    case "alreadySignedIn":
    case "alreadyLinked":
    case "notSignedIn":
    case "wrongProvider":
    case "alreadyVerified":
    case "appleCredentialUnavailable":
    case "emailsUnavailable":
    case "unknown":
      return "transport.unreachable"; // C9
  }
}

/** The client's own check of an address's shape, before any call: Firebase's (`fakeAuth.ts` `looksLikeEmail`), no
 *  stricter. A fuller check is Firebase's and the server's, which answer C5 too. */
export function looksLikeEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+$/.test(email.trim());
}
